import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import { DocumentFileType } from '@risksmart-app/domain/src/types/consts/document-file-type';
import { VersionStatus } from '@risksmart-app/domain/src/types/consts/version-status';
import type {
  InputMaybe,
  UpdateDocumentVersionMutation,
  Version_Status_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useChangeRequestConfirm } from 'src/providers/change-request-confirm-context';
import { useTRPC } from 'src/utils/trpc';

import { withChangeRequestConfirm } from '../_with-change-request-confirm';
import { invalidateChangeRequestFeeds } from '../invalidateChangeRequestFeeds';
import { unwrapApprovalGated } from '../unwrapApprovalGated';

interface UpdateDocumentVersionTRPCSharedInput {
  Id: string;
  LatestModifiedAtTimestamp: string;
  Version: string;
  Status: Version_Status_Enum;
  Summary?: InputMaybe<string>;
  ReasonForReview?: InputMaybe<string>;
  ReviewedBy?: InputMaybe<string>;
  ReviewDate?: InputMaybe<string>;
  NextReviewDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

type UpdateDocumentVersionTRPCTypeInput =
  | { Type: 'file'; FileId: string }
  | { Type: 'html'; Content: string }
  | { Type: 'link'; Link: string };

export type UpdateDocumentVersionTRPCInput =
  UpdateDocumentVersionTRPCSharedInput & UpdateDocumentVersionTRPCTypeInput;

/**
 * Maps the version-status GraphQL enum value to the domain enum the tRPC
 * procedure expects. Both share identical string values; this is a typed
 * lookup rather than a cast.
 */
const toVersionStatus = (status: Version_Status_Enum): VersionStatus => {
  switch (status) {
    case 'archived':
      return VersionStatus.Archived;
    case 'draft':
      return VersionStatus.Draft;
    case 'pending_approval':
      return VersionStatus.PendingApproval;
    case 'published':
      return VersionStatus.Published;
  }
};

interface UpdateDocumentVersionTRPCSharedPayload {
  Id: string;
  LatestModifiedAtTimestamp: string;
  Version: string;
  Status: VersionStatus;
  Summary?: InputMaybe<string>;
  ReasonForReview?: InputMaybe<string>;
  ReviewedBy?: InputMaybe<string>;
  ReviewDate?: InputMaybe<string>;
  NextReviewDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

type UpdateDocumentVersionTRPCPayload = UpdateDocumentVersionTRPCSharedPayload &
  (
    | { Type: DocumentFileType.File; FileId: string }
    | { Type: DocumentFileType.Html; Content: string }
    | { Type: DocumentFileType.Link; Link: string }
  );

const toTRPCPayload = (
  variables: UpdateDocumentVersionTRPCInput
): UpdateDocumentVersionTRPCPayload => {
  const shared: UpdateDocumentVersionTRPCSharedPayload = {
    Id: variables.Id,
    LatestModifiedAtTimestamp: variables.LatestModifiedAtTimestamp,
    Version: variables.Version,
    Status: toVersionStatus(variables.Status),
    Summary: variables.Summary,
    ReasonForReview: variables.ReasonForReview,
    ReviewedBy: variables.ReviewedBy,
    ReviewDate: variables.ReviewDate,
    NextReviewDate: variables.NextReviewDate,
    CustomAttributeData: variables.CustomAttributeData,
  };
  switch (variables.Type) {
    case 'file':
      return {
        ...shared,
        Type: DocumentFileType.File,
        FileId: variables.FileId,
      };
    case 'html':
      return {
        ...shared,
        Type: DocumentFileType.Html,
        Content: variables.Content,
      };
    case 'link':
      return {
        ...shared,
        Type: DocumentFileType.Link,
        Link: variables.Link,
      };
  }
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 * so consumers can be migrated 1:1. The legacy GraphQL contract returned
 * `{ affected_rows: number }`; the tRPC response carries
 * `{ Id, ModifiedAtTimestamp }` but consumers only branch on success/error.
 */
const mapTrpcResponseToGraphQL = (_trpcData: {
  Id: string;
  ModifiedAtTimestamp: string;
}): UpdateDocumentVersionMutation => {
  return {
    updateDocumentVersion: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  };
};

export const useUpdateDocumentVersionTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const changeRequestConfirm = useChangeRequestConfirm();
  const mutation = useMutation({
    ...trpc.frontend.documentFile.update.mutationOptions({
      onSuccess: async (result) => {
        // RSP-5789 — when the mutation was gated into a change request
        // (created or amended), surface it on the CR read surfaces
        // immediately instead of waiting for the panel's next poll.
        if (result.status !== 'applied') {
          await invalidateChangeRequestFeeds(queryClient, trpc);
        }
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.documentFile.documentFilesByDocumentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.documentFile.latestDocumentFile.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.documentFile.documentFileById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.documentFile.publicDocumentFiles.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.documentFile.latestPublicDocumentFileByDocumentId.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `document` (version changes
        // affect the parent document's current-version surfaces).
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.document.pathKey(),
        });
      },
    }),
  });

  return {
    updateDocumentVersion: async (
      variables: UpdateDocumentVersionTRPCInput
    ): Promise<UpdateDocumentVersionMutation> => {
      const result = await withChangeRequestConfirm({
        input: toTRPCPayload(variables),
        run: (v) => mutation.mutateAsync(v),
        confirm: changeRequestConfirm,
      });

      // A confirmed update resolves as `applied` OR `change-request-created` /
      // `change-request-amended` (a CR was raised) — all success, like
      // useDeleteRiskTRPC. expectApplied would throw on the CR branch and
      // surface a false "Failed to save changes" toast. Echo the input Id +
      // timestamp on the CR branch (the version is unchanged).
      return mapTrpcResponseToGraphQL(
        unwrapApprovalGated(result, {
          Id: variables.Id,
          ModifiedAtTimestamp: variables.LatestModifiedAtTimestamp,
        })
      );
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
